/**
 * Factures reçues saisies à la main (décision du propriétaire du 15 octobre 2026) :
 * un ticket de restaurant, un plein, un péage, une facture de fournitures. La pièce
 * est photographiée, les montants recopiés par une personne ; MyEquip la range avec
 * les factures fournisseurs et l'envoie à Pennylane pour que la TVA soit récupérée.
 *
 * Les catégories ci-dessous sont des valeurs génériques, communes à tous les métiers ;
 * aucune n'est propre à une entreprise.
 */
import { z } from 'zod';
import { vatCents, type BasisPoints, type Cents, type VatRateBp } from './units.js';

export const EXPENSE_CATEGORIES = [
  'repas',
  'carburant',
  'peage_parking',
  'fournitures',
  'materiel',
  'sous_traitance',
  'autre',
] as const;
export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];

export const EXPENSE_CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  repas: 'Repas',
  carburant: 'Carburant',
  peage_parking: 'Péage, parking',
  fournitures: 'Fournitures',
  materiel: 'Matériel',
  sous_traitance: 'Sous-traitance',
  autre: 'Autre',
};

/**
 * Moyen de paiement. « Avance personnelle » signale une note de frais : la personne
 * a payé de sa poche. MyEquip ne gère pas le remboursement ; il le signale.
 */
export const EXPENSE_PAYMENT_METHODS = ['carte_entreprise', 'especes', 'avance_personnelle', 'autre'] as const;
export type ExpensePaymentMethod = (typeof EXPENSE_PAYMENT_METHODS)[number];

export const EXPENSE_PAYMENT_LABELS: Record<ExpensePaymentMethod, string> = {
  carte_entreprise: 'Carte de l’entreprise',
  especes: 'Espèces de l’entreprise',
  avance_personnelle: 'Payé de ma poche',
  autre: 'Autre (virement, chèque…)',
};

/** Taux proposés sur un ticket ; `null` = plusieurs taux, la TVA se recopie du ticket. */
export const EXPENSE_VAT_RATES_BP = [2000, 1000, 550, 0] as const satisfies readonly VatRateBp[];
export type ExpenseVatRateBp = (typeof EXPENSE_VAT_RATES_BP)[number];
const isExpenseVatRate = (value: number): value is ExpenseVatRateBp =>
  (EXPENSE_VAT_RATES_BP as readonly number[]).includes(value);

/** Bornes serveur d'une saisie (une dépense n'est pas une facture d'équipement). */
export const EXPENSE_LIMITS = {
  /** 1 000 000 € TTC au plus. */
  maxTtcCents: 100_000_000,
  numberMaxLength: 60,
  noteMaxLength: 300,
  supplierNameMaxLength: 120,
  /** Saisies par personne et par jour. */
  perUserDaily: 100,
  /** Une pièce plus ancienne ne sert plus à récupérer la TVA. */
  maxAgeYears: 3,
  pageSize: 50,
} as const;

/** Délai avant l'envoi automatique à Pennylane : le temps de corriger une faute de frappe. */
export const EXPENSE_PUSH_DELAY_MINUTES = 10;

export const isExpenseReport = (method: string | null | undefined): boolean =>
  method === 'avance_personnelle';

/**
 * HT et TVA d'un montant TTC à un taux unique, au centime, de sorte que
 * HT + TVA = TTC ET TVA = arrondi(HT × taux) quand un tel HT existe : c'est ce que
 * Pennylane recalcule ligne par ligne. Sinon, le HT arrondi le plus proche.
 */
export function splitTtc(ttcCents: Cents, vatBp: BasisPoints): { htCents: Cents; vatCents: Cents } {
  const guess = Math.round((ttcCents * 10000) / (10000 + vatBp));
  for (const ht of [guess, guess - 1, guess + 1]) {
    if (ht >= 0 && ht + vatCents(ht, vatBp) === ttcCents) return { htCents: ht, vatCents: ttcCents - ht };
  }
  return { htCents: guess, vatCents: ttcCents - guess };
}

/**
 * Référence interne d'une pièce sans numéro (ticket de caisse) : stable, datée pour
 * le comptable, unique dans l'entreprise. Elle sert de numéro chez Pennylane.
 * L'identifiant entier, pas un préfixe : deux pièces du même jour ne se confondent jamais.
 */
export function expenseReference(id: string, invoiceDate: string): string {
  return `DEP-${invoiceDate.slice(0, 10).replace(/-/g, '')}-${id.replace(/-/g, '').toUpperCase()}`;
}

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  }, 'Date invalide.');
const cents = z.number().int().min(0).max(EXPENSE_LIMITS.maxTtcCents);

/** Champs saisis, communs à la création et à la modification avant envoi. */
export const expenseEntryFields = z.object({
  supplierId: z.guid().optional(),
  supplierName: z.string().trim().min(1).max(EXPENSE_LIMITS.supplierNameMaxLength).optional(),
  invoiceDate: isoDate,
  invoiceNumber: z.string().trim().max(EXPENSE_LIMITS.numberMaxLength).optional(),
  totalTtcCents: cents.refine((value) => value > 0, 'Le montant doit être positif.'),
  totalVatCents: cents,
  vatRateBp: z.number().int().refine(isExpenseVatRate, 'Taux de TVA non proposé pour une dépense.').nullable(),
  category: z.enum(EXPENSE_CATEGORIES),
  paymentMethod: z.enum(EXPENSE_PAYMENT_METHODS),
  paidBy: z.guid().optional(),
  interventionId: z.guid().nullable().optional(),
  note: z.string().trim().max(EXPENSE_LIMITS.noteMaxLength).optional(),
});

function coherent(
  v: { supplierId?: string | undefined; supplierName?: string | undefined; totalTtcCents: number; totalVatCents: number },
  ctx: z.RefinementCtx,
): void {
  if (!v.supplierId === !v.supplierName) {
    ctx.addIssue({ code: 'custom', path: ['supplierId'], message: 'Choisissez un commerçant ou tapez son nom.' });
  }
  if (v.totalVatCents >= v.totalTtcCents && v.totalTtcCents > 0) {
    ctx.addIssue({ code: 'custom', path: ['totalVatCents'], message: 'La TVA doit être inférieure au total TTC.' });
  }
}

export const expenseEntryBody = expenseEntryFields
  .extend({
    source: z.literal('saisie'),
    scanFileId: z.guid(),
    idempotencyKey: z.guid(),
    /** Même commerçant, même date, même montant : enregistrer quand même. */
    confirmDuplicate: z.boolean().optional(),
  })
  .strict()
  .superRefine(coherent);
export type ExpenseEntryBody = z.infer<typeof expenseEntryBody>;

export const expenseUpdateBody = expenseEntryFields
  .extend({ scanFileId: z.guid().optional() })
  .strict()
  .superRefine(coherent);
export type ExpenseUpdateBody = z.infer<typeof expenseUpdateBody>;
